import { FC, useCallback, useState, useEffect, useRef } from 'react'
import { EXTENSION_CHANNEL } from '@numeo/types-dispatcher-ui'
import { KeyRound, Landmark, LogIn } from 'lucide-react'
import { Button, Input, Modal, Spinner } from 'components/ds2'
import {
    FactoringProvider,
    PROVIDER_CONFIGS,
    CREDENTIAL_BASED_PROVIDERS,
    NO_AUTH_PROVIDERS,
    openFactoringAuthTab,
    isExtensionInstalled,
    clearFactoringStatusCache,
    fetchFactoringStatus,
    FactoringStatusResponse,
    saveWinfactorCredentials,
    saveGsquaredCredentials,
    saveCapitalDepotCredentials,
    saveCompassCredentials,
    saveTafsCredentials,
    saveHaulPayCredentials,
    saveOtrSolutionsCredentials,
    saveFlexentCredentials,
    saveBasicblockCredentials,
} from 'services/factoring'
import { useSnackbar } from 'contexts/SnackbarContext'
import { ExtensionRequiredModal } from './ExtensionRequiredModal'
import { SnackbarType } from 'utils/snackbar'

const AUTH_STATUS_FAST_POLL_INTERVAL_MS = 1500
const AUTH_STATUS_SLOW_POLL_INTERVAL_MS = 10_000
const AUTH_STATUS_FAST_POLL_ATTEMPTS = 5
const AUTH_STATUS_MAX_POLL_ATTEMPTS = 35

const isProviderConnected = (status: FactoringStatusResponse['data'] | null, provider: FactoringProvider): boolean => {
    if (!status) return false

    // Triumph SFTP shares Triumph's browser authentication status. Every other provider value
    // matches its key in the combined factoring status response.
    const statusProvider = provider === FactoringProvider.TRIUMPH_SFTP ? FactoringProvider.TRIUMPH : provider
    const providerStatus = status[statusProvider]
    return typeof providerStatus === 'object' && providerStatus !== null && 'connected' in providerStatus && providerStatus.connected && providerStatus.isValid
}

interface FactoringAuthDialogProps {
    open: boolean
    onClose: () => void
    provider: FactoringProvider
    onSuccess?: () => void
    description?: string
    tokenPurpose?: 'accounting' | 'rating'
}

type CredentialsSaveFn = (username: string, password: string) => Promise<{ success: boolean; error?: string }>

const CREDENTIALS_SAVE_FNS: Partial<Record<FactoringProvider, CredentialsSaveFn>> = {
    winfactor: saveWinfactorCredentials,
    gsquared: saveGsquaredCredentials,
    capital_depot: saveCapitalDepotCredentials,
    compass: saveCompassCredentials,
    tafs: saveTafsCredentials,
    haul_pay: saveHaulPayCredentials,
    otr_solutions: saveOtrSolutionsCredentials,
    flexent: saveFlexentCredentials,
    basicblock: saveBasicblockCredentials,
}

export const FactoringAuthDialog: FC<FactoringAuthDialogProps> = ({ open, onClose, provider, onSuccess, description, tokenPurpose }) => {
    const config = PROVIDER_CONFIGS[provider]
    const isCredentialsBased = CREDENTIAL_BASED_PROVIDERS.includes(provider)
    // Defense-in-depth: providers in NO_AUTH_PROVIDERS (e.g. Engaged Financial) have no loginUrl
    // and don't accept credentials. Rendering this dialog for them would show an empty form.
    const isNoAuthProvider = NO_AUTH_PROVIDERS.includes(provider)
    const [isLoading, setIsLoading] = useState(false)
    const [isWaitingForAuth, setIsWaitingForAuth] = useState(false)
    // The click reported the extension as the cause, so show install guidance instead of a red
    // snackbar the user cannot act on. Set only from a real attempt — nothing is probed up front.
    const [extensionMissing, setExtensionMissing] = useState(false)
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const channelRef = useRef<BroadcastChannel | null>(null)
    const sawDisconnectedStatusRef = useRef(false)
    // Guards against a success and a late error arriving in the same tick on the same channel (e.g. the
    // auth tab is closed right after a successful connect) — whichever lands first wins, the other is ignored.
    const resolvedRef = useRef(false)
    const { showSnackbar } = useSnackbar()
    const onSuccessRef = useRef(onSuccess)
    const showSnackbarRef = useRef(showSnackbar)
    const providerNameRef = useRef(config.name)

    useEffect(() => {
        onSuccessRef.current = onSuccess
        showSnackbarRef.current = showSnackbar
        providerNameRef.current = config.name
    }, [onSuccess, showSnackbar, config.name])

    // Reset form state when dialog opens/closes
    useEffect(() => {
        if (!open) {
            setUsername('')
            setPassword('')
            setIsWaitingForAuth(false)
            setExtensionMissing(false)
        }
    }, [open])

    const completeAuthorization = useCallback(() => {
        if (resolvedRef.current) return
        resolvedRef.current = true
        clearFactoringStatusCache()
        setIsWaitingForAuth(false)
        onSuccessRef.current?.()
    }, [])

    const failAuthorization = useCallback((message?: string) => {
        if (resolvedRef.current) return
        resolvedRef.current = true
        setIsWaitingForAuth(false)
        showSnackbarRef.current(SnackbarType.ERROR, message || `Failed to connect ${providerNameRef.current}. Please try again.`)
    }, [])

    // Listen for the extension's fast-path event and poll the backend as a delivery-safe fallback.
    // BroadcastChannel is intentionally best-effort: a stale/missing content script can lose its
    // one-shot message even though the extension already saved the token successfully.
    useEffect(() => {
        if (!isWaitingForAuth || isCredentialsBased) return

        let cancelled = false
        let pollTimer: ReturnType<typeof setTimeout> | undefined
        let pollAttempts = 0

        channelRef.current = new BroadcastChannel(EXTENSION_CHANNEL)

        channelRef.current.onmessage = (event) => {
            const { type, provider: authProvider, message } = event.data || {}
            if (authProvider !== provider || resolvedRef.current) return

            if (type === 'FACTORING_AUTH_SUCCESS') {
                completeAuthorization()
            } else if (type === 'FACTORING_AUTH_ERROR') {
                failAuthorization(message)
            }
        }

        const schedulePoll = () => {
            if (cancelled || resolvedRef.current || pollAttempts >= AUTH_STATUS_MAX_POLL_ATTEMPTS) return
            const delay = pollAttempts < AUTH_STATUS_FAST_POLL_ATTEMPTS ? AUTH_STATUS_FAST_POLL_INTERVAL_MS : AUTH_STATUS_SLOW_POLL_INTERVAL_MS
            pollTimer = setTimeout(() => void pollStatus(), delay)
        }

        const pollStatus = async () => {
            pollAttempts++
            const status = await fetchFactoringStatus(tokenPurpose ?? 'rating')
            if (cancelled || resolvedRef.current) return

            const connected = isProviderConnected(status, provider)
            if (status && !connected) sawDisconnectedStatusRef.current = true
            if (connected && sawDisconnectedStatusRef.current) {
                completeAuthorization()
                return
            }

            schedulePoll()
        }

        schedulePoll()

        return () => {
            cancelled = true
            if (pollTimer) clearTimeout(pollTimer)
            if (channelRef.current) {
                channelRef.current.close()
                channelRef.current = null
            }
        }
    }, [isWaitingForAuth, isCredentialsBased, provider, tokenPurpose, completeAuthorization, failAuthorization])

    // Tab-based auth
    const handleAuthorize = useCallback(async () => {
        setIsLoading(true)
        resolvedRef.current = false
        clearFactoringStatusCache()

        const initialStatus = await fetchFactoringStatus(tokenPurpose ?? 'rating')
        sawDisconnectedStatusRef.current = !!initialStatus && !isProviderConnected(initialStatus, provider)

        const response = await openFactoringAuthTab(provider, tokenPurpose)

        if (!response.success) {
            // `chrome.runtime.lastError` fires both when the extension is absent and when an installed
            // MV3 worker was simply asleep, so `reason` alone cannot separate them. Reading a file out
            // of the extension package can, without touching the worker — otherwise a dispatcher who
            // already has the extension gets sent off to install it.
            if (response.reason === 'extension_unavailable' && !(await isExtensionInstalled())) {
                setExtensionMissing(true)
                setIsLoading(false)
                return
            }
            // Either an ordinary failure, or the extension is installed and the message merely missed
            // a sleeping worker. Both are worth retrying here, and the extension-required copy that
            // ships with that reason would be a lie, so it is not reused.
            const message = response.reason === 'extension_unavailable' ? undefined : response.error
            showSnackbar(SnackbarType.ERROR, message || 'Failed to open authentication tab. Please try again.')
            setIsLoading(false)
            return
        }

        setIsWaitingForAuth(true)
        setIsLoading(false)
    }, [provider, tokenPurpose, showSnackbar])

    // Credentials-based auth
    const handleCredentialsSubmit = useCallback(async () => {
        if (!username.trim() || !password.trim()) {
            showSnackbar(SnackbarType.ERROR, 'Please enter your username and password')
            return
        }

        const saveFn = CREDENTIALS_SAVE_FNS[provider]
        if (!saveFn) return

        setIsLoading(true)
        const result = await saveFn(username.trim(), password.trim())
        setIsLoading(false)

        if (result.success) {
            clearFactoringStatusCache()
            onSuccess?.()
            onClose()
        } else {
            showSnackbar(SnackbarType.ERROR, result.error || `Failed to connect ${config.name}. Please check your credentials.`)
        }
    }, [username, password, provider, config.name, onSuccess, onClose, showSnackbar])

    const handleCancel = useCallback(() => {
        if (channelRef.current) {
            channelRef.current.close()
            channelRef.current = null
        }
        setIsWaitingForAuth(false)
        onClose()
    }, [onClose])

    const handleModalClose = isWaitingForAuth ? () => undefined : onClose

    // Stable identity on purpose: the modal lists this in the dep arrays of its analytics effect and
    // its detection poll, so an inline arrow would re-fire EXTENSION_REQUIRED and restart the 2s
    // interval on every parent render.
    const handleExtensionDetected = useCallback(() => setExtensionMissing(false), [])

    if (isNoAuthProvider) return null

    if (extensionMissing) {
        return <ExtensionRequiredModal open={open} onClose={onClose} onExtensionDetected={handleExtensionDetected} analyticsFlow="factoring" />
    }

    return (
        <Modal
            open={open}
            onClose={handleModalClose}
            size="sm"
            showCloseButton={false}
            header={
                <div className="flex items-center gap-3">
                    <Landmark className="h-5 w-5 text-accent" aria-hidden />
                    <h2 className="text-base font-semibold text-foreground">
                        {isWaitingForAuth ? `Waiting for ${config.name} Login...` : `${config.name} Authorization Required`}
                    </h2>
                </div>
            }
            footer={
                isWaitingForAuth ? (
                    <Button type="button" variant="ghost" onClick={handleCancel}>
                        Cancel
                    </Button>
                ) : (
                    <>
                        <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
                            Cancel
                        </Button>
                        {isCredentialsBased ? (
                            <Button
                                type="button"
                                onClick={handleCredentialsSubmit}
                                prefix={isLoading ? <Spinner size={16} color="current" /> : <KeyRound className="h-4 w-4" aria-hidden />}
                                disabled={isLoading || !username.trim() || !password.trim()}
                            >
                                {isLoading ? 'Connecting...' : `Connect ${config.name}`}
                            </Button>
                        ) : (
                            <Button
                                type="button"
                                onClick={handleAuthorize}
                                prefix={isLoading ? <Spinner size={16} color="current" /> : <LogIn className="h-4 w-4" aria-hidden />}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Opening...' : `Open ${config.name} Login`}
                            </Button>
                        )}
                    </>
                )
            }
        >
            {isCredentialsBased ? (
                <>
                    <p className="mb-2 text-sm leading-relaxed text-foreground-muted">{description || config.description}</p>
                    <div className="flex flex-col gap-4">
                        {/* Non-semantic name + disabled autofill so Chrome/1Password/LastPass don't inject the
                            dispatcher's own Numeo login into the factor's credential fields. See CredentialsModal.tsx. */}
                        <Input
                            label="Username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            name="numeo-factoring-account"
                            autoComplete="off"
                            data-lpignore="true"
                            data-1p-ignore="true"
                            disabled={isLoading}
                            onKeyDown={(e) => e.key === 'Enter' && void handleCredentialsSubmit()}
                        />
                        <Input
                            label="Password"
                            inputType="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            name="numeo-factoring-secret"
                            autoComplete="new-password"
                            data-lpignore="true"
                            data-1p-ignore="true"
                            disabled={isLoading}
                            onKeyDown={(e) => e.key === 'Enter' && void handleCredentialsSubmit()}
                        />
                    </div>
                </>
            ) : isWaitingForAuth ? (
                <div className="py-2">
                    <div className="mb-4 flex items-center justify-center gap-3">
                        <span className="h-5 w-5 animate-spin rounded-full border-2 border-accent border-t-transparent" aria-hidden />
                        <p className="text-sm text-foreground-muted">Waiting for authentication to complete...</p>
                    </div>
                    <div className="rounded-xl bg-accent-soft p-4 text-[13px] text-accent-soft-foreground">
                        Please complete the login in the {config.name} tab. This dialog will close automatically once connected.
                    </div>
                </div>
            ) : (
                <>
                    <p className="mb-2 text-sm leading-relaxed text-foreground-muted">{description || config.description}</p>
                    <div className="rounded-xl bg-default p-4 text-[13px] text-foreground-muted">
                        <p className="mb-2 text-sm font-medium text-foreground">What happens next:</p>
                        <p className="mb-1">• A new tab will open to {config.name}&apos;s secure login page</p>
                        <p className="mb-1">• Log in with your {config.name} credentials</p>
                        <p>• This dialog will close automatically once connected</p>
                    </div>
                </>
            )}
        </Modal>
    )
}
